/**
 * Formats a phone number for display.
 *
 * The field carries no country context (country lives on the Address entity, not on
 * the contact/person the phone belongs to), so US/NANP numbers are detected purely
 * from the digit shape, and international numbers are kept essentially as typed —
 * just lightly normalized, never regrouped into a country-specific format.
 *
 *   4155551234            -> (415) 555-1234
 *   14155551234           -> (415) 555-1234   (drops the NANP leading 1)
 *   +14155551234          -> (415) 555-1234
 *   +44 20 7946 0958      -> +44 20 7946 0958  (kept as typed)
 *   24155551234           -> 24155551234       (11-digit non-NANP: left as typed)
 *   '' / null / undefined -> ''
 */
export function formatPhoneNumber(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return ''
  }

  const trimmed = value.trim()
  if (trimmed === '') {
    return ''
  }

  const hasPlus = trimmed.startsWith('+')
  const digits = trimmed.replace(/\D/g, '')

  if (hasPlus) {
    // A '+1' followed by a full 11-digit (1 + 10) number is North American (NANP) —
    // render it in US national format. Any other country code, or a partial '+1',
    // is treated as international.
    if (digits.length === 11 && digits[0] === '1') {
      return formatNanpNational(digits.slice(1))
    }
    return normalizeInternational(trimmed)
  }

  // No '+': only treat as US/NANP when the shape is unambiguous.
  if (digits.length === 10) {
    return formatNanpNational(digits)
  }
  if (digits.length === 11 && digits[0] === '1') {
    return formatNanpNational(digits.slice(1))
  }

  // Partial entry, or an 11-digit non-'1' number — leave it as typed rather than
  // mangling it into a US shape.
  return trimmed
}

function formatNanpNational(tenDigits: string): string {
  return (
    `(${tenDigits.slice(0, 3)})` +
    ` ${tenDigits.slice(3, 6)}` +
    `-${tenDigits.slice(6, 10)}`
  )
}

function normalizeInternational(trimmed: string): string {
  // Keep the number essentially as typed: a single leading '+', the user's own
  // grouping (spaces/dashes/dots/parens) preserved, with runs of internal whitespace
  // collapsed to single spaces. No country-specific regrouping is invented.
  const withoutLeadingPluses = trimmed.replace(/^\++/, '')
  return '+' + withoutLeadingPluses.replace(/\s+/g, ' ').trim()
}
